import { cn } from "@/lib/utils"

function Skeleton({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("skeleton-shimmer rounded-md bg-primary/10", className)}
      data-slot="skeleton"
      {...props}
    />
  )
}

type TableSkeletonProps = {
  columns?: number
  rows?: number
  showSelection?: boolean
  showFilters?: boolean
  className?: string
}

function TableSkeleton({
  columns = 8,
  rows = 5,
  showSelection = true,
  showFilters = true,
  className,
}: TableSkeletonProps) {
  return (
    <div className={cn("flex flex-col gap-2 bg-card text-card-foreground rounded-md p-2 border", className)}>
      {/* Actions and Column Toggle */}
      <div className="flex justify-end gap-2">
        <Skeleton className="h-9 w-[100px]" />
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <Skeleton className="h-8 w-[250px]" />
        <Skeleton className="h-8 w-[200px]" />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <div className="relative w-full overflow-auto">
          <table className="w-full caption-bottom text-sm">
            <thead className="bg-muted">
              <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                {showSelection && (
                  <th className="h-12 w-[40px] px-4 align-middle">
                    <Skeleton className="h-4 w-4" />
                  </th>
                )}
                {Array.from({ length: columns }).map((_, colIdx) => (
                  <th
                    key={colIdx}
                    className="h-12 px-4 align-middle font-medium text-muted-foreground"
                  >
                    <div className={cn("flex h-full flex-col justify-center", showFilters ? "gap-2" : undefined)}>
                      <Skeleton className="h-4 w-[120px]" />
                      {showFilters && <Skeleton className="h-8 w-[150px]" />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, rowIdx) => (
                <tr
                  key={rowIdx}
                  className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                >
                  {showSelection && (
                    <td className="w-[40px] p-4 align-middle">
                      <Skeleton className="h-4 w-4" />
                    </td>
                  )}
                  {Array.from({ length: columns }).map((_, cellIdx) => (
                    <td key={cellIdx} className="p-4 align-middle">
                      <Skeleton className="h-4 w-[120px]" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Pagination */}
      <div className="flex items-center justify-between px-2">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-8 w-[200px]" />
      </div>
    </div>
  )
}

function TransferListSkeleton() {
  return (
    <div className="flex w-full flex-col items-start justify-center space-y-4 p-8 md:flex-row md:space-y-0 md:space-x-4">
      {/* Source List Skeleton */}
      <div className="flex-1">
        <div className="flex flex-col rounded-md border bg-card">
          <div className="border-b p-4">
            <div className="mb-2 h-6 w-40"><Skeleton /></div>
          </div>
          <div className="p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mb-2 flex items-center space-x-2 rounded-md px-2 py-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-32 flex-1" />
                <Skeleton className="h-4 w-4 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Transfer Buttons Skeleton */}
      <div className="flex flex-row items-center justify-center gap-2 md:w-16 md:flex-col">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-10 rounded-full" />
        ))}
      </div>
      {/* Target List Skeleton */}
      <div className="flex-1">
        <div className="flex flex-col rounded-md border bg-card">
          <div className="border-b p-4">
            <div className="mb-2 h-6 w-40"><Skeleton /></div>
          </div>
          <div className="p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mb-2 flex items-center space-x-2 rounded-md px-2 py-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-32 flex-1" />
                <Skeleton className="h-4 w-4 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export { Skeleton, TableSkeleton, TransferListSkeleton }
